import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PillTabs } from '../admin/PillTabs';
import { formatDate } from '../admin/format';
import { ProTag } from '../plan/ProTag';
import { openUnlockDialog } from '../plan/plan-limit-store';
import { RANGE_PRESETS, RangeKey, dayMs, earliestReadableDay, presetDays, utcDay } from './period';

export interface PeriodValue {
  key: RangeKey;
  /** YYYY-MM-DD, set when `key` is 'custom'. */
  start?: string;
  end?: string;
}

const toLocalDate = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const fromLocalDate = (date: Date) => utcDay(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

/**
 * The period a report reads: a few presets as pills, and a custom range in a
 * popover. Every period is compared with the one just before it, so the
 * control says which days — the page says against what.
 *
 * `maxDays` is the plan's history window (Free keeps 30). Presets that reach
 * further stay on the control, marked Pro, and a click on one says why rather
 * than choosing it; the calendar will not pick a day before the window.
 */
export function PeriodControl({
  value,
  onChange,
  presets = RANGE_PRESETS,
  maxDays,
  className,
}: {
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  presets?: Exclude<RangeKey, 'custom'>[];
  /** Days of history the plan can read; absent or -1 is unlimited. */
  maxDays?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(
    value.start && value.end ? { from: toLocalDate(value.start), to: toLocalDate(value.end) } : undefined,
  );

  const limited = !!maxDays && maxDays > 0;
  const earliest = limited ? earliestReadableDay(maxDays) : undefined;
  const beyond = (key: Exclude<RangeKey, 'custom'>) => limited && presetDays(key) > (maxDays as number);

  const custom = value.key === 'custom' && value.start && value.end;
  const customLabel = custom
    ? `${formatDate(new Date(dayMs(value.start!)))} – ${formatDate(new Date(dayMs(value.end!)))}`
    : t('VIZ_ANALYTICS.PERIOD.CUSTOM');

  return (
    <div className={cn('flex max-w-full flex-wrap items-center gap-2', className)} data-testid="period-control">
      <PillTabs<RangeKey>
        value={custom ? ('custom' as RangeKey) : value.key}
        onChange={(key) => onChange({ key })}
        ariaLabel={t('VIZ_ANALYTICS.PERIOD.LABEL')}
        options={presets.map((key) => ({
          value: key,
          label: beyond(key) ? (
            <span className="inline-flex items-center gap-1">
              {t(`VIZ_ANALYTICS.PERIOD.PRESET_${key.toUpperCase()}`)}
              <ProTag />
            </span>
          ) : (
            t(`VIZ_ANALYTICS.PERIOD.PRESET_${key.toUpperCase()}`)
          ),
          disabled: beyond(key),
          onDisabledClick: () => openUnlockDialog({ quotaType: 'analyticsHistoryDays', limit: maxDays }),
        }))}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={cn('h-8 gap-1.5 text-xs', custom ? 'text-foreground' : 'text-muted-foreground')}>
            <CalendarRange className="size-3.5" />
            <span className="max-w-[180px] truncate tabular-nums">{customLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-3">
          <p className="px-1 text-sm font-medium">{t('VIZ_ANALYTICS.PERIOD.PICK_RANGE')}</p>
          <Calendar
            mode="range"
            selected={draft}
            onSelect={setDraft}
            numberOfMonths={1}
            defaultMonth={draft?.from ?? new Date()}
            disabled={earliest ? [{ after: new Date() }, { before: toLocalDate(earliest) }] : { after: new Date() }}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              {t('VIZ_ANALYTICS.COMMON.CANCEL')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!draft?.from}
              onClick={() => {
                if (!draft?.from) return;
                onChange({ key: 'custom', start: fromLocalDate(draft.from), end: fromLocalDate(draft.to ?? draft.from) });
                setOpen(false);
              }}
            >
              {t('VIZ_ANALYTICS.PERIOD.APPLY')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
